import { EMBED, MODAL, TOOLTIP, BACKDROP, LAYOUT_TRAITS } from 'lib/trait';
import {
  BLOCK,
  STACK,
  BUTTON,
  TEXT,
  IMAGE,
  HERO,
  IMAGE_WITH_TEXT,
  SINGLE_SELECT,
  MULTI_SELECT,
  RATING_EMOJI,
  RATING_NUMBER,
  RATING_STAR,
  RATING,
  TEXT_INPUT,
} from 'lib/block';
import {
  addTextTheme,
  addImageTheme,
  addButtonTheme,
  addHeroTheme,
  addImageWithTextTheme,
  addOptionSelectTheme,
  addTextInputTheme,
  addEditorMappingTheme,
  addModalTheme,
  addTooltipTheme,
  addEmbedTheme,
  addBackdropTheme,
  textOverrides,
  imageOverrides,
  buttonOverrides,
  heroOverrides,
  imageWithTextOverrides,
  optionSelectOverrides,
  textInputOverrides,
  editorMappingOverrides,
  extractTextStyles,
  extractImageStyles,
  extractButtonStyles,
  extractHeroStyles,
  extractImageWithTextStyles,
  extractOptionSelectStyles,
  extractTextInputStyles,
  extractEditorMappingStyles,
} from './content';
import { untheme } from './content/utils';

export const EDITOR_MAPPING = 'EDITOR_MAPPING';

/**
 * Returns the search prefix for a theme filter
 * @param {string} type - Type to filter
 * @returns {string} Prefix for a theme filter
 */
export const themeFilterPrefix = type => {
  switch (type) {
    case EMBED:
      return 'container.embed';
    case MODAL:
      return 'container.modal';
    case TOOLTIP:
      return 'container.tooltip';
    case TEXT:
      return 'text';
    case BUTTON:
      return 'button';
    case HERO:
      return 'hero';
    case IMAGE:
      return 'image';
    case IMAGE_WITH_TEXT:
      return 'imagewithtext';
    case SINGLE_SELECT:
    case MULTI_SELECT:
    case RATING_EMOJI:
    case RATING_NUMBER:
    case RATING_STAR:
    case RATING:
      return 'select';
    case TEXT_INPUT:
      return 'textinput';
    default:
      return '';
  }
};

export const defaultThemeVariant = type => {
  const prefix = themeFilterPrefix(type);
  if (prefix !== '') {
    return `${prefix}.default`;
  }
  return null;
};

/**
 * Returns the eligible theme variants for a given type and theme
 * @param {string} type - Type of content
 * @param {Object} compiledTheme - Theme object containing content styles
 * @returns {Array} Array of eligible theme variants
 */
export const eligibleThemeVariants = (type, compiledTheme) => {
  const prefix = themeFilterPrefix(type);

  return (
    Object.keys(compiledTheme?.content?.components || {})
      // Exclude nested styles that match the prefix (eg button text)
      .filter(
        key =>
          key.startsWith(prefix) &&
          key.slice(prefix.length + 1).split('.').length === 1
      )
  );
};

/**
 * Applies a theme to a content chunk. If we know exactly what variant to apply, we can pass in selectedVariant.
 * If we don't know what variant to apply, we can pass in theme and we will check if the current variant for the content exists in the theme and keep it or fall back to the default variant.
 * @param {Object} type - Type of content
 * @param {Object} content - Content object
 * @param {string} selectedVariant - Selected variant path
 * @param {Object} compiledTheme - Compiled theme object
 * @param {boolean} preserveOverrides - Whether to preserve existing styles as overrides
 */
export const updateToThemed = ({
  type,
  content,
  selectedVariant,
  compiledTheme,
  preserveOverrides,
}) => {
  if (!selectedVariant && !compiledTheme) {
    return content;
  }

  const currentTheme = content.style?.theme;
  const themeHasCurrentThemeVariant =
    currentTheme && !!compiledTheme?.content.components[currentTheme];
  const variantToApply =
    selectedVariant ??
    (themeHasCurrentThemeVariant ? currentTheme : defaultThemeVariant(type));

  // Block and Stack are passthrough types that don't require a selectedVariant because can determine the variant to apply based on their content
  if (!variantToApply && ![BLOCK, STACK].includes(type)) {
    return content;
  }

  const themeAdders = {
    /* Content blocks */
    [TEXT]: addTextTheme,
    [IMAGE]: addImageTheme,
    [BUTTON]: addButtonTheme,
    [HERO]: addHeroTheme,
    [IMAGE_WITH_TEXT]: addImageWithTextTheme,
    [SINGLE_SELECT]: addOptionSelectTheme('option'),
    [MULTI_SELECT]: addOptionSelectTheme('option'),
    [RATING_EMOJI]: addOptionSelectTheme(RATING_EMOJI),
    [RATING_NUMBER]: addOptionSelectTheme(RATING_NUMBER),
    [RATING_STAR]: addOptionSelectTheme(RATING_STAR),
    [TEXT_INPUT]: addTextInputTheme,
    /* Trait editor mapping */
    [EDITOR_MAPPING]: addEditorMappingTheme,
    /* Themeable trait models used by updateStepGroupToTheme */
    [MODAL]: addModalTheme,
    [TOOLTIP]: addTooltipTheme,
    [EMBED]: addEmbedTheme,
    [BACKDROP]: addBackdropTheme,
    /* Passthrough blocks used by updateStepGroupToTheme */
    [BLOCK]: () => ({
      ...content,
      content: updateToThemed({
        type: content.content?.ratingType ?? content.blockType,
        content: content.content,
        selectedVariant: variantToApply,
        compiledTheme,
        preserveOverrides,
      }),
    }),
    [STACK]: () => ({
      ...content,
      items: content.items.map(item =>
        updateToThemed({
          type: item.type,
          content: item,
          selectedVariant: variantToApply,
          compiledTheme,
          preserveOverrides,
        })
      ),
    }),
  };

  return themeAdders[type]
    ? themeAdders[type](content, variantToApply, preserveOverrides)
    : content;
};

export const updateStepChildToTheme = ({
  stepChild,
  compiledTheme,
  preserveOverrides = false,
}) => {
  return {
    ...stepChild,
    content: updateToThemed({
      type: stepChild.content.type,
      content: stepChild.content,
      compiledTheme,
      preserveOverrides,
    }),
  };
};

export const updateStepGroupToTheme = ({
  stepGroup,
  compiledTheme,
  preserveOverrides = false,
}) => {
  const { traits, children } = stepGroup;
  const layoutTrait = traits.find(trait => LAYOUT_TRAITS.includes(trait.type));
  const themeHasVariantFromTrait =
    !!compiledTheme.content.components[layoutTrait?.config.style.theme];
  const traitThemeVariant = themeHasVariantFromTrait
    ? layoutTrait?.config.style.theme
    : defaultThemeVariant(layoutTrait.type);

  return {
    ...stepGroup,
    traits: traits.map(trait =>
      updateToThemed({
        type: trait.type,
        content: trait,
        selectedVariant: traitThemeVariant,
        preserveOverrides,
      })
    ),
    children: children.map(stepChild =>
      updateStepChildToTheme({ stepChild, compiledTheme, preserveOverrides })
    ),
  };
};

/**
 * Applies a theme to an object
 *
 * @param {Object} compiledTheme - Theme object containing content styles
 * @param {*} chunk - Chunk (object) to apply theme to
 * @returns {Object} Combined object with theme styles merged with base object
 */
export const applyThemeStyles = (compiledTheme, chunk) => {
  if (!compiledTheme || !chunk || typeof chunk !== 'object') {
    return chunk;
  }

  if (Array.isArray(chunk)) {
    return chunk.map(item => applyThemeStyles(compiledTheme, item));
  }

  const themed = {
    ...(chunk.theme
      ? {
          // theme first so that any overrides in the chunk are applied last
          ...compiledTheme?.content?.components?.[chunk?.theme],
          ...chunk,
        }
      : chunk),
  };

  // Recursively apply theme to all nested objects
  Object.keys(themed).forEach(key => {
    if (themed[key] && typeof themed[key] === 'object') {
      themed[key] = applyThemeStyles(compiledTheme, themed[key]);
    }
  });

  return themed;
};

/**
 * Removes all the styles from theme recursively
 *
 * @param {Object} compiledTheme - Theme object containing content styles
 * @param {*} chunk - Chunk (object) to apply theme to
 * @returns {Object} Combined object with theme styles merged with base object
 */
export const stripThemeStyles = (compiledTheme, chunk) => {
  if (!chunk || typeof chunk !== 'object') {
    return chunk;
  }

  if (Array.isArray(chunk)) {
    return chunk.map(item => stripThemeStyles(compiledTheme, item));
  }

  const unthemed = untheme(compiledTheme, chunk);

  // Recursively check all nested objects
  Object.keys(unthemed).forEach(key => {
    if (unthemed[key] && typeof unthemed[key] === 'object') {
      unthemed[key] = stripThemeStyles(compiledTheme, unthemed[key]);
    }
  });

  return unthemed;
};

/**
 * Finds the overrides for a given content object and organizes them for resetting
 * @param {Object} compiledTheme - Theme object containing content styles
 * @param {string} type - Type of content
 * @param {Object} content - Content object (either block data or editor object, depending on the type)
 * @returns {Array} paths - Array of override paths with unique key, UI label, and array of path(s) to update
 */
export const mapOverrides = ({ compiledTheme, type, content }) => {
  const overrides = {
    [TEXT]: textOverrides,
    [IMAGE]: imageOverrides,
    [BUTTON]: buttonOverrides,
    [HERO]: heroOverrides,
    [IMAGE_WITH_TEXT]: imageWithTextOverrides,
    [SINGLE_SELECT]: optionSelectOverrides,
    [MULTI_SELECT]: optionSelectOverrides,
    [RATING_EMOJI]: optionSelectOverrides,
    [RATING_NUMBER]: optionSelectOverrides,
    [RATING_STAR]: optionSelectOverrides,
    [TEXT_INPUT]: textInputOverrides,
    [EDITOR_MAPPING]: editorMappingOverrides,
  };

  return overrides[type]({ compiledTheme, content });
};

export const extractStylesForThemeVariant = ({
  compiledTheme,
  type,
  content,
}) => {
  const extractors = {
    [TEXT]: extractTextStyles,
    [IMAGE]: extractImageStyles,
    [BUTTON]: extractButtonStyles,
    [HERO]: extractHeroStyles,
    [IMAGE_WITH_TEXT]: extractImageWithTextStyles,
    [SINGLE_SELECT]: extractOptionSelectStyles,
    [MULTI_SELECT]: extractOptionSelectStyles,
    [RATING_EMOJI]: extractOptionSelectStyles,
    [RATING_NUMBER]: extractOptionSelectStyles,
    [RATING_STAR]: extractOptionSelectStyles,
    [TEXT_INPUT]: extractTextInputStyles,
    [EDITOR_MAPPING]: extractEditorMappingStyles,
  };

  return extractors[type](stripThemeStyles(compiledTheme, content), type);
};
